import { useState, useEffect, useCallback, useMemo } from 'react';
import { getProperties, getFeaturedProperties, getPropertyById } from '@/services/propertyService';

/**
 * Gayrimenkul listesi hook'u
 * @param {Object} filters - Filtreleme seçenekleri
 * @returns {{ properties: Array, loading: boolean, error: string|null, refetch: Function }}
 */
export function useProperties(filters = {}) {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ESLint react-hooks/exhaustive-deps: karmaşık ifadeyi ayrı değişkene çek
  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);

  const fetchProperties = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getProperties(filters);
      setProperties(data);
    } catch (err) {
      console.error('Error fetching properties:', err);
      setError(err.message || 'Failed to fetch properties');
    } finally {
      setLoading(false);
    }
    // filtersKey değiştiğinde callback yeniden oluşturulur;
    // filters referansı her renderda değişebilir, filtersKey stabil kalır
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  return { properties, loading, error, refetch: fetchProperties };
}

/**
 * Öne çıkan gayrimenkuller hook'u
 * @returns {{ properties: Array, loading: boolean, error: string|null, refetch: Function }}
 */
export function useFeaturedProperties() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFeatured = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getFeaturedProperties();
      setProperties(data);
    } catch (err) {
      console.error('Error fetching featured properties:', err);
      setError(err.message || 'Failed to fetch featured properties');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeatured();
  }, [fetchFeatured]);

  return { properties, loading, error, refetch: fetchFeatured };
}

/**
 * Tek bir gayrimenkul detay hook'u
 * @param {string|null} id - Property UUID
 * @returns {{ property: Object|null, loading: boolean, error: string|null, refetch: Function }}
 */
export function useProperty(id) {
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProperty = useCallback(async () => {
    if (!id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await getPropertyById(id);
      setProperty(data);
    } catch (err) {
      console.error('Error fetching property:', err);
      setError(err.message || 'Failed to fetch property');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProperty();
  }, [fetchProperty]);

  return { property, loading, error, refetch: fetchProperty };
}
